import { useState } from 'react';
import { useFirestore } from '../../firebase/useFirestore';
import { db } from '../../firebase/config.js';
import useAutosaveField from '../../hooks/useAutosaveField';
import AutosaveStatus from '../common/AutosaveStatus';
import ToggleButton from '../common/ToggleButton';
import Table from '../common/table/Table.jsx';
import TableCell from '../common/table/TableCell.jsx';
import { LABEL_COLORS, newCustomLabelKey } from '../../helpers/adressHelpers/resolveAdressLabels';
import { COLOR_NAMES } from '../../helpers/colorPalette';

/** Ruim voor een eigen woord, krap genoeg voor een chip in een tabelrij. */
const MAX_LABEL_LENGTH = 30;

/**
 * Het instellingenblok voor labels van een organisatie: per label een eigen
 * naam, een kleur en 'Verbergen', plus '+ Label toevoegen'. Gedeeld door
 * 'Labels bij huishoudens' (AdressLabelsConfig) en 'Labels bij projecten'
 * (ProjectLabelsConfig); alleen het veld op CompagnyMeta en de tekst verschillen.
 *
 * Verbergen en niet verwijderen: wat gelabeld is draagt de SLEUTEL, en die
 * blijft staan. Een verborgen label is hier nog te zien (grijs) en weer aan te zetten.
 * AI-CONTEXT: See docs/features/planner-overzicht-bkb.md
 *
 * @param {object} props
 * @param {string} props.field - Het veld op CompagnyMeta: een map per labelsleutel
 * @param {string} props.heading - De kop boven het blok
 * @param {string} props.intro - De uitleg onder de kop
 * @param {(meta: object, opties: {includeHidden: boolean}) => Array<{key: string, label: string, color: string, builtIn: boolean, hidden?: boolean}>} props.resolve - Leest de labels uit CompagnyMeta
 * @param {string} [props.fixedKey] - Een label dat niet te verbergen is
 * @returns {JSX.Element}
 */
const LabelsConfig = ({ field: FIELD, heading, intro, resolve, fixedKey }) => {
    const [adding, setAdding] = useState(false);
    const compagny = useFirestore('CompagnyMeta');
    const { save, status } = useAutosaveField('CompagnyMeta');

    const comp = Array.isArray(compagny) ? compagny[0] : undefined;
    const rows = comp ? resolve(comp, { includeHidden: true }) : [];

    /** @param {string} key @param {string} value */
    const labelHandler = (key, value) => save(comp.docid, { [`${FIELD}.${key}.Label`]: value });

    /** @param {string} key @param {string} color */
    const colorHandler = (key, color) => {
        if (!LABEL_COLORS.includes(color)) return;
        save(comp.docid, { [`${FIELD}.${key}.Color`]: color });
    };

    // Een knopklik, geen toetsaanslag: direct schrijven, niet via de debounce
    // van useAutosaveField — de nieuwe rij moet meteen verschijnen.
    // Eén label per klik, ook bij een dubbele klik: een label is alleen te
    // verbergen, nooit te verwijderen, dus een tweede rij blijft voor altijd staan.
    // AI-CONTEXT: See docs/pitfalls.md#state-multi-step-signup-leaves-half-an-account
    const addLabelHandler = async () => {
        if (adding) return;
        setAdding(true);
        const key = newCustomLabelKey();
        // Achteraan: Position telt door op wat er al staat.
        const position = rows.filter((r) => !r.builtIn).length + 1;
        try {
            await db.collection('CompagnyMeta').doc(comp.docid).update({
                [`${FIELD}.${key}`]: { Label: 'Nieuw label', Color: 'grey', Position: position, Hidden: 'false' },
            });
        } finally {
            setAdding(false);
        }
    };

    return (
        <div className="dashboard-container">
            <h2>{heading}</h2>
            <p>{intro}</p>
            {!comp ? (
                <p className="status-config-loading">Instellingen worden geladen…</p>
            ) : (
                <>
                    <AutosaveStatus status={status} reserve />
                    <Table
                        columns={[
                            { key: 'label', label: 'NAAM', searchable: false },
                            { key: 'color', label: 'KLEUR', searchable: false },
                            { key: 'hidden', label: 'VERBERGEN', searchable: false },
                        ]}
                        items={rows}
                        getItemKey={(row) => row.key}
                        showSearch={false}
                        tableClassName="status-config-table"
                        getRowClassName={(row) => (row.hidden ? 'adress-labels-config__row--hidden' : '')}
                        renderRow={(row) => (
                            <>
                                <TableCell>
                                    {/* Ongecontroleerd veld met defaultValue, zie StatusConfigTable.
                                        AI-CONTEXT: See docs/pitfalls.md#ui-tabletitlecell-onchange-refocus */}
                                    <input
                                        type="text"
                                        maxLength={MAX_LABEL_LENGTH}
                                        defaultValue={row.label}
                                        onChange={(e) => labelHandler(row.key, e.target.value)}
                                        aria-label={`Naam van het label '${row.label}'`}
                                    />
                                </TableCell>
                                <TableCell>
                                    <span className={`label-chip label-chip--${row.color}`}>{row.label}</span>
                                    <select
                                        value={row.color}
                                        onChange={(e) => colorHandler(row.key, e.target.value)}
                                        aria-label={`Kleur van het label '${row.label}'`}
                                    >
                                        {LABEL_COLORS.map((c) => <option key={c} value={c}>{COLOR_NAMES[c]}</option>)}
                                    </select>
                                </TableCell>
                                <TableCell>
                                    {fixedKey && row.key === fixedKey ? (
                                        <span className="adress-labels-config__fixed">Altijd aan</span>
                                    ) : (
                                        <ToggleButton
                                            docid={comp.docid}
                                            id={`${FIELD}.${row.key}.Hidden`}
                                            collection={'CompagnyMeta'}
                                            value={comp[FIELD]?.[row.key]?.Hidden}
                                        />
                                    )}
                                </TableCell>
                            </>
                        )}
                    />
                    <button type="button" className="adress-labels-config__add" onClick={addLabelHandler} disabled={adding}>
                        + Label toevoegen
                    </button>
                </>
            )}
        </div>
    );
};

export default LabelsConfig;
